// main-search.js — full replacement

class MainSearch extends HTMLElement {
  constructor() {
    super();
    this.oldSearchValue = '';
    this._boundOnKeyUp = null;
    this._boundOnChange = null;
    this._escHandler = null;
    this.searchTimeoutID = -1;
    this.searchAbortController = null;
  }

  connectedCallback() {
    // Close buttons
    this.querySelectorAll('.main-search__close').forEach((el) => {
      el.addEventListener('click', this._onCloseClick);
    });

    // Esc anywhere inside the element
    this._escHandler = (evt) => {
      if (evt.key === 'Escape') this._close();
    };
    this.addEventListener('keyup', this._escHandler);

    // Quick search
    if (this.dataset.quickSearch === 'true') this.initQuickSearch();
  }

  disconnectedCallback() {
    // Remove close listeners
    this.querySelectorAll('.main-search__close').forEach((el) => {
      el.removeEventListener('click', this._onCloseClick);
    });
    if (this._escHandler) this.removeEventListener('keyup', this._escHandler);

    // Remove input listeners
    const searchInput = this.querySelector('.main-search__input');
    if (searchInput && this._boundOnKeyUp)  searchInput.removeEventListener('keyup', this._boundOnKeyUp);
    if (searchInput && this._boundOnChange) searchInput.removeEventListener('change', this._boundOnChange);

    // Cancel pending work
    clearTimeout(this.searchTimeoutID);
    if (this.searchAbortController) this.searchAbortController.abort('MainSearch disconnected');
  }

  _onCloseClick = (evt) => {
    if (evt) evt.preventDefault();
    this._close();
  };

  _close() {
    document.body.classList.remove('show-search');
  }

  initQuickSearch() {
    const searchInput = this.querySelector('.main-search__input');
    if (!searchInput) return;

    const resultsBox = this.querySelector('.main-search__results');
    if (!resultsBox) return;

    const searchTimeoutThrottle = 500;
    const resultLimit = 8;
    const includeMeta = this.dataset.quickSearchMeta === 'true';

    const handleInputChange = () => {
      const valueToSearch = searchInput.value.trim();

      // Clear state if empty
      if (!valueToSearch.length) {
        this.oldSearchValue = '';
        if (this.searchAbortController) {
          this.searchAbortController.abort('Cleared');
          this.searchAbortController = null;
        }
        clearTimeout(this.searchTimeoutID);
        this.classList.remove('main-search--has-results', 'main-search--results-on-multiple-lines', 'main-search--loading');
        resultsBox.innerHTML = '';
        // Keep footer at bottom (JS will ensure it's in container)
        this._ensureFooterLink();
        return;
      }

      // Only search if changed
      if (valueToSearch === this.oldSearchValue) {
        // Still update footer link target with latest input, in case user edited then reverted
        this._ensureFooterLink();
        return;
      }
      this.oldSearchValue = valueToSearch;

      // Abort prior fetch
      if (this.searchAbortController) {
        this.searchAbortController.abort('Superseded');
        this.searchAbortController = null;
      }
      clearTimeout(this.searchTimeoutID);

      // Build "view all" URL from the form
      const form = searchInput.closest('form');
      const linkURL = new URL(form ? form.action : (window.location.origin + '/search'));
      const formParams = form ? new URLSearchParams(new FormData(form)) : new URLSearchParams();
      // Ensure q matches the current input value (FormData should already include it, but enforce)
      formParams.set('q', valueToSearch);
      formParams.forEach((value, key) => linkURL.searchParams.set(key, value));

      // Show loading
      this.classList.remove('main-search--has-results', 'main-search--results-on-multiple-lines', 'main-search--no-results');
      this.classList.add('main-search--loading');
      if (!this.querySelector('.main-search__results-spinner')) {
        resultsBox.innerHTML = '<div class="main-search__results-spinner"><div class="loading-spinner"></div></div>';
      }

      // Throttled fetch
      this.searchTimeoutID = setTimeout(() => {
        this.searchAbortController = new AbortController();

        let ajaxUrl;
        const usePredictive =
          !!(window.theme && theme.Shopify && theme.Shopify.features && theme.Shopify.features.predictiveSearch);

        if (usePredictive) {
          ajaxUrl = new URL(theme.routes.base + theme.routes.predictiveSearch);
          ajaxUrl.searchParams.set('q', valueToSearch);
          ajaxUrl.searchParams.set('section_id', 'predictive-search');
          ajaxUrl.searchParams.set('resources[type]', 'product');
          ajaxUrl.searchParams.set('resources[limit]', resultLimit);
          ajaxUrl.searchParams.set(
            'resources[options][fields]',
            includeMeta ? 'title,product_type,variants.title,vendor,tag,variants.sku' : 'title,product_type,variants.title,vendor'
          );
        } else {
          ajaxUrl = new URL(linkURL);
          ajaxUrl.searchParams.set('section_id', 'main-search');
        }

        fetch(ajaxUrl, { method: 'GET', signal: this.searchAbortController.signal })
          .then((r) => {
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            return r.text();
          })
          .then((html) => {
            const template = document.createElement('template');
            template.innerHTML = html;

            // Find the grid in the response
            const resultsList = usePredictive
              ? template.content.querySelector('.product-grid')
              : template.content.querySelector('.section-search-template .product-grid');

            // Reset UI
            this.searchAbortController = null;
            this.classList.remove('main-search--has-results', 'main-search--results-on-multiple-lines', 'main-search--no-results');
            resultsBox.innerHTML = '';

            if (!resultsList) {
              this.classList.add('main-search--no-results');
              const emptyMessage = document.createElement('div');
              emptyMessage.className = 'main-search__empty-message';
              emptyMessage.innerHTML =
                (window.theme && theme.strings && theme.strings.generalSearchNoResultsWithoutTerms) || 'No results.';
              resultsBox.appendChild(emptyMessage);
              this.classList.remove('main-search--loading');
              // Update footer link even if no results
              this._ensureFooterLink(linkURL.toString());
              return;
            }

            // Build containers
            const resultsProducts = document.createElement('div');
            resultsProducts.className = 'main-search__results__products collection-listing';
            const gridWrap = document.createElement('div');
            gridWrap.className = resultsList.className;
            resultsProducts.appendChild(gridWrap);

            const resultsPages = document.createElement('div');
            resultsPages.className = 'main-search__results__pages';

            // Product results (limit)
            const productBlocks = resultsList.querySelectorAll('.product-block:not(.collection-block):not(.page-block)');
            productBlocks.forEach((block, index) => {
              if (index >= resultLimit) return; // keep under limit
              block.classList.add('main-search-result');
              block.querySelectorAll('.btn.quickbuy-toggle').forEach((el) => el.remove());
              block.querySelectorAll('.quickbuy-toggle').forEach((el) => el.classList.remove('quickbuy-toggle'));
              gridWrap.appendChild(block);
            });

            // Page/article results
            resultsList.querySelectorAll('.product-block.page-block').forEach((block) => {
              const a = block.querySelector('a');
              const title = block.querySelector('.page-block__title');
              if (!a || !title) return;

              const item = document.createElement('a');
              item.className = 'main-search-result main-search-result--page';
              item.href = a.href;
              item.innerHTML = '<div class="main-search-result__text"></div>';
              item.firstElementChild.textContent = title.innerText;
              resultsPages.appendChild(item);
            });

            this.classList.remove('main-search--loading');

            const hasProducts = !!resultsProducts.querySelector('.main-search-result');
            const hasPages = !!resultsPages.querySelector('.main-search-result');

            if (hasProducts || hasPages) {
              this.classList.add('main-search--has-results');
              this.classList.toggle(
                'main-search--results-on-multiple-lines',
                resultsProducts.querySelectorAll('.product-block').length > 4
              );

              if (hasProducts) resultsBox.appendChild(resultsProducts);
              if (hasPages) {
                const heading = document.createElement('h6');
                heading.className = 'main-search-result__heading';
                heading.innerHTML = (window.theme && theme.strings && theme.strings.generalSearchPages) || 'Pages';
                resultsPages.insertAdjacentElement('afterbegin', heading);
                resultsBox.appendChild(resultsPages);
              }
            } else {
              this.classList.add('main-search--no-results');
              const emptyMessage = document.createElement('div');
              emptyMessage.className = 'main-search__empty_message';
              emptyMessage.textContent =
                (window.theme && theme.strings && theme.strings.generalSearchNoResultsWithoutTerms) || 'No results.';
              resultsBox.appendChild(emptyMessage);
            }

            // Ensure footer is present and at bottom, with up-to-date link
            this._ensureFooterLink(linkURL.toString());
          })
          .catch((err) => {
            if (err && err.name === 'AbortError') return;
            console.warn('[MainSearch] fetch error:', err);
            this.classList.remove('main-search--loading');
          });
      }, searchTimeoutThrottle);
    };

    this._boundOnKeyUp = handleInputChange;
    this._boundOnChange = handleInputChange;
    searchInput.addEventListener('keyup', this._boundOnKeyUp);
    searchInput.addEventListener('change', this._boundOnChange);

    // Initial footer wiring
    this._ensureFooterLink();
  }

  /**
   * Ensures there is a single footer as the last child of .main-search__container,
   * and updates the "View all" link href if provided.
   */
  _ensureFooterLink(href) {
    const containerEl = this.querySelector('.main-search__container');
    if (!containerEl) return;

    let footer = this.querySelector('.main-search__footer');
    if (!footer) {
      footer = document.createElement('div');
      footer.className = 'main-search__footer';

      const left = document.createElement('span');
      left.className = 'main-search__footer-left';
      left.textContent = 'Search';

      const rightLink = document.createElement('a');
      rightLink.className = 'main-search__footer-right';
      rightLink.href = href || this._buildCurrentSearchURL() || '/search';
      rightLink.textContent =
        (window.theme && theme.strings && theme.strings.generalSearchViewAll) || 'View all search results';

      footer.appendChild(left);
      footer.appendChild(rightLink);
    } else if (href) {
      const rightLinkEl = footer.querySelector('.main-search__footer-right');
      if (rightLinkEl) rightLinkEl.href = href;
    }

    // Append/move to be the last child so it stays at the bottom
    if (footer.parentElement !== containerEl) {
      containerEl.appendChild(footer);
    } else {
      containerEl.appendChild(footer); // re-append to ensure it's last
    }
  }

  /**
   * Builds a search URL based on the current form and input value.
   */
  _buildCurrentSearchURL() {
    const input = this.querySelector('.main-search__input');
    const form = input ? input.closest('form') : null;
    if (!form) return null;

    const linkURL = new URL(form.action, window.location.origin);
    const params = new URLSearchParams(new FormData(form));
    if (input && input.value) params.set('q', input.value);
    params.forEach((v, k) => linkURL.searchParams.set(k, v));
    return linkURL.toString();
  }
}

window.customElements.define('main-search', MainSearch);
